import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { Check, MessageCircle, Play, ShoppingBag } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import MagneticButton from "@/components/MagneticButton";
import { useLanguage } from "@/context/LanguageContext";
import { useCart, singleWhatsappUrl } from "@/context/CartContext";
import { getProduct, sizes, getSize, defaultSizeId, flagshipId, formatPrice } from "@/config/products";

// Detect touch device once — evitamos aplicar tilt en móvil.
const canHover = () => {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(hover: hover) and (pointer: fine)").matches;
};

// Helpers para srcset de Unsplash (los productos usan images.unsplash.com).
function buildUnsplashSrc(url, w) {
  if (typeof url !== "string" || !url.includes("images.unsplash.com")) return url;
  try {
    const u = new URL(url);
    u.searchParams.set("w", String(w));
    u.searchParams.set("q", u.searchParams.get("q") || "78");
    u.searchParams.set("fm", u.searchParams.get("fm") || "webp");
    u.searchParams.set("auto", u.searchParams.get("auto") || "format");
    return u.toString();
  } catch {
    return url;
  }
}
function buildSrcSet(url) {
  if (typeof url !== "string" || !url.includes("images.unsplash.com")) return undefined;
  return [640, 900, 1200, 1600]
    .map((w) => `${buildUnsplashSrc(url, w)} ${w}w`)
    .join(", ");
}

export default function FlagshipProduct() {
  const { t } = useLanguage();
  const { addItem } = useCart();
  const product = getProduct(flagshipId);
  const [sizeId, setSizeId] = useState(defaultSizeId);
  const [qty, setQty] = useState(1);
  const [activeImg, setActiveImg] = useState(0); // int index | "video"
  const [added, setAdded] = useState(false);
  const videoRef = useRef(null);

  // Pause + reset video when switching away
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (activeImg === "video") {
      try { v.currentTime = 0; v.play(); } catch {}
    } else {
      try { v.pause(); v.currentTime = 0; } catch {}
    }
  }, [activeImg]);

  const size = getSize(sizeId);
  const price = size.price;

  // Pointer tilt sutil (max ±4°) — solo desktop con hover real.
  const cardRef = useRef(null);
  const rotX = useMotionValue(0);
  const rotY = useMotionValue(0);
  const springRotX = useSpring(rotX, { stiffness: 220, damping: 22, mass: 0.4 });
  const springRotY = useSpring(rotY, { stiffness: 220, damping: 22, mass: 0.4 });
  const tRotX = useTransform(springRotX, (v) => `${v}deg`);
  const tRotY = useTransform(springRotY, (v) => `${v}deg`);

  const onPointerMove = (e) => {
    if (!canHover()) return;
    const el = cardRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width;   // 0..1
    const py = (e.clientY - rect.top) / rect.height;   // 0..1
    const maxDeg = 3.5;
    rotY.set((px - 0.5) * 2 * maxDeg);   // horizontal → rotateY
    rotX.set(-(py - 0.5) * 2 * maxDeg);  // vertical   → rotateX (invertido)
  };
  const onPointerLeave = () => {
    rotX.set(0);
    rotY.set(0);
  };

  const handleAdd = () => {
    addItem(product, size.label, price, qty);
    setAdded(true);
    setTimeout(() => setAdded(false), 1300);
  };

  const order = () =>
    window.open(
      singleWhatsappUrl({ productKey: product.key, size: size.label, qty, price }, t),
      "_blank"
    );

  return (
    <section id="flagship" data-testid="flagship-section" className="bg-white py-20 lg:py-28">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-5 lg:grid-cols-2 lg:gap-16 lg:px-10">
        {/* Galería */}
        <Reveal y={40}>
          <motion.div
            ref={cardRef}
            onPointerMove={onPointerMove}
            onPointerLeave={onPointerLeave}
            style={{
              rotateX: tRotX,
              rotateY: tRotY,
              transformStyle: "preserve-3d",
              transformPerspective: 1200,
            }}
            className="reflect-sweep overflow-hidden rounded-2xl border border-[#eee] bg-[#f7f7f5]"
          >
            <AnimatePresence mode="wait">
              {activeImg === "video" && product.video ? (
                <motion.video
                  key="video"
                  ref={videoRef}
                  data-testid="flagship-video"
                  src={product.video}
                  poster={product.videoPoster || product.images[0]}
                  autoPlay
                  muted
                  loop
                  playsInline
                  controls={false}
                  onError={() => { /* codec/network fail — fall back silently */ }}
                  initial={{ opacity: 0, scale: 1.04, filter: "blur(6px)" }}
                  animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
                  exit={{ opacity: 0, scale: 0.98, filter: "blur(4px)" }}
                  transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  className="aspect-square w-full object-cover"
                />
              ) : (
                <motion.img
                  key={`${product.images[activeImg] || product.images[0]}-${sizeId}`}
                  data-testid="flagship-image"
                  src={buildUnsplashSrc(product.images[activeImg] || product.images[0], 1600)}
                  srcSet={buildSrcSet(product.images[activeImg] || product.images[0])}
                  sizes="(max-width: 1024px) 92vw, 640px"
                  alt={t.products[product.key].name}
                  initial={{ opacity: 0, scale: 1.04, rotate: -1.6, filter: "blur(6px)" }}
                  animate={{ opacity: 1, scale: 1, rotate: 0, filter: "blur(0px)" }}
                  exit={{ opacity: 0, scale: 0.98, rotate: 1.2, filter: "blur(4px)" }}
                  transition={{
                    duration: 0.5,
                    ease: [0.22, 1, 0.36, 1],
                    filter: { duration: 0.35 },
                  }}
                  className="aspect-square w-full object-cover"
                />
              )}
            </AnimatePresence>
          </motion.div>
          {(product.images.length > 1 || product.video) && (
            <div className="mt-4 flex gap-3">
              {product.images.map((img, i) => (
                <button
                  key={i}
                  data-testid={`flagship-thumb-${i}`}
                  onClick={() => setActiveImg(i)}
                  className={`h-20 w-20 overflow-hidden rounded-xl border transition-[border-color,transform] duration-300 press ${
                    activeImg === i ? "border-[#111]" : "border-[#e8e8e8] hover:border-[#9a9a9a]"
                  }`}
                >
                  <img
                    src={buildUnsplashSrc(img, 240)}
                    srcSet={`${buildUnsplashSrc(img, 160)} 160w, ${buildUnsplashSrc(img, 240)} 240w, ${buildUnsplashSrc(img, 320)} 320w`}
                    sizes="80px"
                    alt=""
                    loading="lazy"
                    decoding="async"
                    className="h-full w-full object-cover"
                  />
                </button>
              ))}
              {product.video && (
                <button
                  data-testid="flagship-thumb-video"
                  onClick={() => setActiveImg("video")}
                  aria-label="Play product video"
                  className={`relative h-20 w-20 shrink-0 overflow-hidden rounded-xl border press transition-[border-color,transform] duration-300 ${
                    activeImg === "video" ? "border-[#111]" : "border-[#e8e8e8] hover:border-[#9a9a9a]"
                  }`}
                >
                  <img
                    src={buildUnsplashSrc(product.videoPoster || product.images[0], 240)}
                    alt=""
                    loading="lazy"
                    className="h-full w-full object-cover"
                  />
                  <span className="absolute inset-0 bg-black/25" />
                  <span className="absolute inset-0 flex items-center justify-center">
                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-white/95 shadow-sm">
                      <Play className="h-3.5 w-3.5 translate-x-[1px] fill-[#111] text-[#111]" />
                    </span>
                  </span>
                  <span className="absolute bottom-1 left-1/2 -translate-x-1/2 rounded bg-black/60 px-1.5 py-0.5 font-body text-[8px] font-semibold uppercase tracking-[0.14em] text-white">
                    Video
                  </span>
                </button>
              )}
            </div>
          )}
        </Reveal>

        {/* Info */}
        <div className="lg:pt-4">
          <Reveal>
            <span className="eyebrow text-[11px] text-[#999]">{t.flagship.eyebrow}</span>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="iridescent-hover mt-3 font-serif-display text-3xl font-medium text-[#111] sm:text-4xl">
              {t.products[product.key].name}
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-4 max-w-md font-body text-sm leading-relaxed text-[#666]">
              {t.flagship.statement}
            </p>
          </Reveal>

          {/* Precio dinámico */}
          <Reveal delay={0.15}>
            <div className="mt-6">
              <div className="flex items-baseline gap-3" aria-live="polite" aria-atomic="true">
                <div className="relative">
                  <AnimatePresence mode="wait">
                    <motion.span
                      key={price}
                      data-testid="flagship-price"
                      initial={{ opacity: 0, y: 4 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -4 }}
                      transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
                      className="block font-serif-display text-4xl text-[#111]"
                    >
                      {formatPrice(price)}
                    </motion.span>
                  </AnimatePresence>
                </div>
                <span className="font-body text-sm text-[#999]">USD</span>
              </div>
              <p data-testid="flagship-sold-as-pair" className="mt-2 font-body text-xs text-[#666]">
                {t.flagship.soldAsPair}
              </p>
            </div>
          </Reveal>

          {/* Selector de tamaño (CTW) — tarjetas con valor, precio y estilo */}
          <Reveal delay={0.2}>
            <div className="mt-7">
              <p className="mb-3 font-body text-xs font-medium uppercase tracking-wider text-[#888]">
                {t.flagship.chooseSize}
              </p>
              <div
                role="radiogroup"
                aria-label={t.flagship.chooseSize}
                className="grid grid-cols-2 gap-3"
              >
                {sizes.map((s) => {
                  const selected = sizeId === s.id;
                  return (
                    <button
                      key={s.id}
                      data-testid={`flagship-size-${s.id}`}
                      onClick={() => setSizeId(s.id)}
                      role="radio"
                      aria-checked={selected}
                      data-selected={selected ? "1" : "0"}
                      className={`variant-chip press relative rounded-xl border px-4 py-3 text-left ${
                        selected
                          ? "border-[#111] bg-[#f5f5f3]"
                          : "border-[#e2e2e2] bg-white hover:border-[#9a9a9a]"
                      }`}
                    >
                      <span className="absolute right-2.5 top-2.5 flex h-4 w-4 items-center justify-center">
                        <Check
                          className={`h-4 w-4 text-[#111] transition-opacity duration-200 ${
                            selected ? "opacity-100" : "opacity-0"
                          }`}
                        />
                      </span>
                      <span className="block font-body text-base font-semibold text-[#111]">{s.label}</span>
                      <span className="mt-0.5 block font-body text-sm text-[#444]">{formatPrice(s.price)}</span>
                      <span className="mt-1 block font-body text-xs text-[#888]">
                        {t.sizeGuide.styleShort[s.id]} · {s.perEarring}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </Reveal>

          {/* Cantidad */}
          <Reveal delay={0.25}>
            <div className="mt-6">
              <p className="mb-3 font-body text-xs font-medium uppercase tracking-wider text-[#888]">
                {t.flagship.quantity}
              </p>
              <div className="flex w-fit items-center rounded-full border border-[#ddd]">
                <button
                  data-testid="flagship-qty-minus"
                  onClick={() => setQty((q) => Math.max(1, q - 1))}
                  className="px-4 py-2 text-lg text-[#666] hover:text-[#111]"
                >
                  −
                </button>
                <span data-testid="flagship-qty" className="w-8 text-center font-body text-[#111]">{qty}</span>
                <button
                  data-testid="flagship-qty-plus"
                  onClick={() => setQty((q) => q + 1)}
                  className="px-4 py-2 text-lg text-[#666] hover:text-[#111]"
                >
                  +
                </button>
              </div>
            </div>
          </Reveal>

          {/* Botones */}
          <Reveal delay={0.3}>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <MagneticButton
                data-testid="flagship-add-cart"
                onClick={handleAdd}
                strength={8}
                radius={90}
                style={{ "--glow-color": "255, 255, 255" }}
                className="tactile-btn cta-breathe flex min-w-[190px] flex-1 items-center justify-center gap-2 rounded-full bg-[#111] px-7 py-3.5 font-body text-sm font-medium text-white hover:bg-[#2a2a2a]"
              >
                {added ? (
                  <>
                    <Check className="h-4 w-4" /> {t.flagship.added}
                  </>
                ) : (
                  <>
                    <ShoppingBag className="h-4 w-4" /> {t.flagship.addToCart}
                  </>
                )}
              </MagneticButton>
              <MagneticButton
                data-testid="flagship-whatsapp"
                onClick={order}
                strength={6}
                radius={80}
                className="tactile-btn flex flex-1 items-center justify-center gap-2 rounded-full border border-[#ddd] px-7 py-3.5 font-body text-sm text-[#111] hover:border-[#111]"
              >
                <MessageCircle className="h-4 w-4" /> {t.flagship.buy}
              </MagneticButton>
            </div>
          </Reveal>

          {/* Especificaciones */}
          <Reveal delay={0.35}>
            <div className="mt-10 border-t border-[#eee] pt-6">
              <h3 className="font-body text-xs font-semibold uppercase tracking-wider text-[#888]">
                {t.flagship.specsTitle}
              </h3>
              <dl className="mt-4 grid grid-cols-2 gap-x-6 gap-y-3">
                {t.flagship.specs.map(([label, value]) => (
                  <div key={label} className="flex justify-between border-b border-[#f2f2f2] pb-2">
                    <dt className="font-body text-sm text-[#999]">{label}</dt>
                    <dd className="font-body text-sm font-medium text-[#222]">{value}</dd>
                  </div>
                ))}
              </dl>
              <p className="mt-4 font-body text-xs leading-relaxed text-[#888]">
                <span className="font-medium text-[#555]">{t.flagship.ctwTitle}:</span> {t.flagship.ctwNote}
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
